import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, Response,
} from '@/types';
import {
  CommercialOffer, CommercialOffers, CommercialOfferTemplate, LogoFile, NewCommercialOffer,
  NewCommercialOfferResponse, NewCommercialOfferTemplate, Organization, ResponseOfferId,
} from '@/modules/commercial-offers/types';

interface CommercialOfferTemplates extends BaseResponse, ApiPagination {
    data: {
      organizations: [{
        id: number;
        name_organization: string;
      }],
      items: CommercialOfferTemplate[],
    }
}

interface CommercialOffers extends BaseResponse, ApiPagination {
  data: {
    clients: string[],
    items: CommercialOffer[],
    has_template: boolean,
  }
}

interface ResponseTemplates extends BaseResponse, ApiPagination {
  data: {
    commercial_proposal_template_id: number;
  }
}

export class CommercialOffersService extends BaseApiService {
  private readonly base_path = 'commercial-proposal';

  async get_offers_templates(filters: FiltersPagination<Filters>): Promise<CommercialOfferTemplates> {
    const url = `${this.base_path}/template`;
    const payload = filters;

    const { data } = await this.http_client.get<CommercialOfferTemplates>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_offer_template_by_id(id: string | number): Promise<Response<NewCommercialOfferTemplate>> {
    const url = `${this.base_path}/template/${id}`;
    const { data } = await this.http_client.get<Response<NewCommercialOfferTemplate>>(url);

    return data;
  }

  async add_offer_template(form_data: FormData): Promise<Response<ResponseTemplates>> {
    const payload = form_data;

    const url = `${this.base_path}/template`;
    const { data } = await this.http_client.post<Response<ResponseTemplates>>(url, payload);

    return data;
  }

  async update_offer_template(id: string | number, form_data: FormData): Promise<Response<ResponseTemplates>> {
    const payload = form_data;

    const url = `${this.base_path}/template/${id}`;
    const { data } = await this.http_client.patch<Response<ResponseTemplates>>(url, payload);

    return data;
  }

  async delete_offer_template(id: string | number): Promise<BaseResponse> {
    const { data } = await this.http_client.delete<BaseResponse>(`${this.base_path}/template/${id}`);

    return data;
  }

  async delete_files(id: string | number, files: number[]): Promise<BaseResponse> {
    const { data } = await this.http_client.delete<BaseResponse>(`${this.base_path}/template/${id}/files`, {
      data: { files },
    });

    return data;
  }

  async active_file(file_id: number): Promise<BaseResponse> {
    const payload = {
      is_active: true,
    };
    const { data } = await this.http_client.patch<BaseResponse>(`${this.base_path}/template/files/${file_id}/active`, payload);

    return data;
  }

  async add_new_offer(value: NewCommercialOffer): Promise<ResponseOfferId> {
    const url = this.base_path;
    const { data } = await this.http_client.post<ResponseOfferId>(url, value);

    return data;
  }

  async get_commercial_offers(filters: FiltersPagination<Filters>): Promise<CommercialOffers> {
    const url = `${this.base_path}`;
    const payload = filters;

    const { data } = await this.http_client.get<CommercialOffers>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_commercial_offer_by_id(id: string | number): Promise<NewCommercialOfferResponse> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<NewCommercialOfferResponse>(url);

    return data;
  }

  async delete_commercial_offer(id: string | number): Promise<BaseResponse> {
    const { data } = await this.http_client.delete<BaseResponse>(`${this.base_path}/${id}`);

    return data;
  }

  async update_commercial_offer(id: string | number, value: NewCommercialOffer): Promise<ResponseOfferId> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.patch<ResponseOfferId>(url, value);

    return data;
  }

  async update_commercial_offer_files(id: number | string, value: FormData) {
    const url = `${this.base_path}/${id}/files`;
    const { data } = await this.http_client.patch<Response<unknown>>(url, value);

    return data;
  }

  async get_catalog_products(payload: CommercialOffers.CatalogProductRequest): Promise<CommercialOffers.CatalogProductResponse> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<CommercialOffers.CatalogProductResponse>(`${this.base_path}/catalog_products`, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }
}

export const default_new_offer = (): NewCommercialOffer => ({
  client_id: 0,
  client_name: '',
  client_contact_full_name: '',
  client_contact_phone: '',
  client_contact_email: '',
  products: [],
  users: [],
  has_template: false,
  is_hide_discount: false,
  is_hide_photo_description: false,
  id: 0,
});

export const default_new_offer_template = (): NewCommercialOfferTemplate => ({
  title: '',
  company_name: '',
  company_phone: '',
  company_email: '',
  company_url: '',
  company_address: '',
  company_description: '',
  organizations: [] as Organization[],
  files: [] as LogoFile[],
});

export const default_catalog_products_filters = (): CommercialOffers.CatalogProductRequestFilters => ({
  search: '',
  categories: [],
  software_type: null,
  equipment_models: [],
});
